<script lang="ts" setup>
import { nextTick, onMounted, ref } from 'vue'
import { NavItem } from '@/helper/Types'
import { debounce } from '@/helper/tools'
import CounterBadge from '@/components/badges/CounterBadge.vue'

defineProps<{
  unreadNewsCount: number
  showTabs?: boolean
}>()

const activeNavItem = ref<NavItem>(NavItem.OVERVIEW)
const filterValue = ref('')
const search = ref<HTMLInputElement | null>(null)
const searchReset = ref<HTMLElement | null>(null)

const emit = defineEmits<{
  (e: 'filterValueChanged', val: string): void
  (e: 'setActiveTab', val: NavItem): void
}>()

onMounted((): void => search.value?.focus())

function navItemClicked(value: NavItem) {
  activeNavItem.value = value
  emit('setActiveTab', activeNavItem.value)
}

function searchChangeHandler(): void {
  emit('filterValueChanged', filterValue.value)
}

const searchInputHandler = debounce(() => {
  searchChangeHandler()
}, 800)

function resetSearch(): void {
  filterValue.value = ''
  emit('filterValueChanged', '')
  nextTick(() => searchChangeHandler())
}
</script>

<template>
  <div class="card overview-navbar">
    <div v-if="showTabs" class="btn-wrapper">
      <button
        :class="{ 'is--tertiary': activeNavItem === NavItem.OVERVIEW }"
        class="btn has-outline is--secondary"
        @click="navItemClicked(NavItem.OVERVIEW)"
      >
        {{ $t('overview.overview') }}
      </button>
      <button
        :class="{ 'is--tertiary': activeNavItem === NavItem.NEWS }"
        class="btn has-outline is--secondary"
        @click="navItemClicked(NavItem.NEWS)"
      >
        <CounterBadge v-if="unreadNewsCount > 0" :count="unreadNewsCount" class="alert" />
        {{ $t('news.news') }}
      </button>
    </div>
    <div v-if="activeNavItem === NavItem.OVERVIEW" class="form__element is--search">
      <input
        id="project-search"
        ref="search"
        v-model="filterValue"
        :placeholder="$t('overview.placeholder_search')"
        tabindex="1"
        type="text"
        @change="searchChangeHandler"
        @input="searchInputHandler"
      >
      <i
        ref="searchReset"
        :class="{ 'is--visible': !!filterValue }"
        class="search__reset ic-times"
        @click="resetSearch"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/style/helpers/include-media';

.overview-navbar {
  margin-bottom: rem-calc(25);
  padding: rem-calc(16);
  display: flex;
  min-height: 72px;

  @include media('<tablet') {
    min-height: auto;
    padding: rem-calc(8);
  }
}

.is--search {
  margin: 0;

  input {
    width: 100%;
    outline: 0;
    border: 0;
    height: 100%;
  }
}

.btn-wrapper {
  display: flex;
  gap: 1rem;

  @include media('<tablet') {
    gap: 0.5rem;
  }
}

.form__element.is--search {
  width: auto;
  flex: 1;
  margin-right: 0;

  &:not(:first-child) {
    margin-left: 1rem;
    padding-left: 1rem;
    border-left: 1px lightgray solid;

    @include media('<tablet') {
      margin-left: 0.5rem;
      padding-left: 0.5rem;
    }

    &:before {
      left: 1.5rem;

      @include media('<tablet') {
        left: 1rem;
      }
    }
  }
}

.btn {
  min-width: auto;
  display: flex;
  align-items: center;
  gap: rem-calc(5);

  @include themify($themes) {
    border-radius: rem-calc(themed('btnSubNavBorderRadius'));
  }
}
</style>
